import type { ReactNode } from "react";

const CONTACT = "info@mindleadsuite.com";

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-2">
      <h2 className="text-lg font-semibold tracking-tight">{title}</h2>
      <div className="space-y-2 text-sm leading-relaxed text-muted-foreground">{children}</div>
    </section>
  );
}

/**
 * Pagina pubblica «Eliminazione dell'account» (rotta /eliminazione-account): richiesta da Google Play
 * (Sicurezza dei dati → indirizzo web per chiedere la cancellazione anche senza installare l'app).
 * Coerente con l'informativa privacy (sezioni 9 e 10): richiesta entro 30 giorni, dati cancellati dopo 90 giorni dalla cessazione.
 */
export default function AccountDeletion() {
  const subject = encodeURIComponent("Richiesta eliminazione account");
  const body = encodeURIComponent(
    "Richiedo l'eliminazione del mio account Mindlead Construction.\n\nEmail dell'account:\nNome dell'azienda:\n",
  );
  return (
    <main className="min-h-screen bg-background">
      <div className="mx-auto max-w-2xl space-y-8 px-5 py-10 pt-[calc(var(--safe-top,0px)+2.5rem)] pb-[calc(var(--safe-bottom,0px)+2.5rem)]">
        <header className="space-y-2">
          <p className="text-xs font-medium uppercase tracking-widest text-primary">Mindlead Construction</p>
          <h1 className="text-2xl font-bold tracking-tight">Eliminazione dell'account e dei dati</h1>
          <p className="text-sm text-muted-foreground">
            Come chiedere la cancellazione del tuo account Mindlead Construction, anche senza avere l'app installata.
          </p>
        </header>

        <Section title="Come richiederla">
          <p>
            <strong className="text-foreground">Dall'app:</strong> icona dell'account in alto a destra → Il mio account →
            Privacy e dati → «Elimina account».
          </p>
          <p>
            <strong className="text-foreground">Senza l'app:</strong> scrivi a{" "}
            <a className="text-primary underline underline-offset-2" href={`mailto:${CONTACT}?subject=${subject}&body=${body}`}>
              {CONTACT}
            </a>{" "}
            dall'indirizzo email dell'account (oppure indicandolo nel messaggio) e il nome dell'azienda. Verifichiamo che la
            richiesta provenga davvero dal titolare dell'account prima di procedere.
          </p>
          <p>La richiesta viene gestita entro 30 giorni e ti confermiamo l'esito via email.</p>
        </Section>

        <Section title="Cosa viene eliminato">
          <p>
            Il tuo profilo di accesso (nome, email, credenziali) e le preferenze personali. I dati inseriti per conto
            dell'azienda (commesse, rapportini, documenti, fatture e simili) appartengono all'azienda cliente: se vuoi
            cancellare anche quelli, la richiesta deve arrivare dall'amministratore dell'azienda.
          </p>
        </Section>

        <Section title="Cosa può essere conservato">
          <p>
            Alla cessazione del rapporto i dati restano disponibili per l'esportazione per 90 giorni, poi vengono
            cancellati in modo irreversibile, salvo i dati che dobbiamo conservare per obblighi di legge (ad esempio
            fiscali e contabili) e le copie di sicurezza, che si sovrascrivono secondo il loro ciclo ordinario. I dati
            diagnostici sono conservati solo per il tempo strettamente necessario.
          </p>
          <p>
            Maggiori dettagli nell'<a className="text-primary underline underline-offset-2" href="/privacy">informativa sulla privacy</a>.
          </p>
        </Section>
      </div>
    </main>
  );
}
